/**
 * "We can't reach the server" state for a protected route.
 *
 * Rendered INSTEAD of bouncing to /login when auth failed because the backend was
 * unreachable (network error / timeout / 5xx) while we still hold a stored token. The
 * session is held, not ended: the token is untouched and a bounded backoff re-attempt runs
 * in the background, so a transient stall self-heals with no user action and no logout.
 *
 * Once the retry budget is spent we stop and say so, with two explicit choices — retry
 * again, or go to /login. There is always an escape hatch, which is what makes the
 * "when unsure, treat a failure as unreachable" default safe.
 */
import { Button, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { authService } from '@/common/bt-auth/auth-service';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import {
  getAuthRetryAttempts,
  isAuthRetryBudgetSpent,
  nextAuthRetryDelayMs,
  recordAuthRetryAttempt,
  resetAuthRetryAttempts,
} from '@/common/bt-auth/auth-retry-policy';
import { debugLog } from '@common/utils/debug-logger';

export interface BackendUnreachableProps {
  /** `?redirect=` search string to carry over if the user opts to sign in instead. */
  loginSearch: string;
  /** Login screen for the current flow — `/clear-login` inside the clear flow. */
  loginPath?: string;
}

export default function BackendUnreachable({ loginSearch, loginPath = '/login' }: BackendUnreachableProps) {
  const dispatch = useAppDispatch();
  // Mirrors the module-scoped attempt counter into render state. The counter lives OUTSIDE
  // the component because each retry flips the status through 'autoJwtStarted' (busy), which
  // unmounts this component — component state would reset to 0 and retry forever.
  const [attempts, setAttempts] = useState(getAuthRetryAttempts());
  const [goToLogin, setGoToLogin] = useState(false);

  useEffect(() => {
    const delay = nextAuthRetryDelayMs(getAuthRetryAttempts());
    if (delay === null) return;
    const handle = setTimeout(() => {
      recordAuthRetryAttempt();
      setAttempts(getAuthRetryAttempts());
      debugLog('login-flow', 'BackendUnreachable: retrying auth', { attempt: getAuthRetryAttempts() });
      // force: true bypasses the "already attempted this token" skip-guard. A failed retry
      // re-settles the same unreachable state; a successful one hydrates the session and the
      // guard renders the app the user was already on.
      void authService.attemptJwtAuth(dispatch, { force: true });
    }, delay);
    return () => clearTimeout(handle);
  }, [dispatch, attempts]);

  if (goToLogin) return <Navigate to={{ pathname: loginPath, search: loginSearch }} replace={true} />;

  const budgetSpent = isAuthRetryBudgetSpent(attempts);

  return (
    <Stack sx={{ width: '100vw', height: '100vh', p: 3 }} spacing={2} justifyContent="center" alignItems="center">
      <Typography variant="h6" textAlign="center">
        {budgetSpent ? "Can't reach the server" : 'Reconnecting…'}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 420 }}>
        {budgetSpent
          ? "You're still signed in — we just can't reach Blueticks right now. This is usually brief."
          : 'The server did not respond. Your session is still active; retrying…'}
      </Typography>
      {budgetSpent && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            onClick={() => {
              resetAuthRetryAttempts();
              setAttempts(0);
            }}
          >
            Try again
          </Button>
          <Button variant="text" onClick={() => setGoToLogin(true)}>
            Sign in instead
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
